/**
 * Jobs Tab Component
 * Manages job assignments, editing, and status updates
 */
import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { CheckCircle, Clock, AlertCircle, Users, Briefcase, Trash2, Edit2, X, MessageCircle, Send, Loader2, XCircle, Info, Plus, Archive, RotateCcw } from "lucide-react";
import { isJobExpired, getStatusColor, getTomorrowSGT } from './constants';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sanitize phone for fallback wa.me link.
function sanitizePhone(raw) {
  if (!raw) return '';
  let digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('0')) digits = digits.replace(/^0+/, '');
  if (digits.length === 8) digits = '65' + digits;
  return digits;
}

function buildWhatsAppMessage(driverName, selectedJobs) {
  // Mirror backend: ONE free-form message per driver, but visually repeat the
  // `Hi <driver>, here is your job for <date>:` header and `— Unity Delivery
  // Tracker` footer around every job so it looks like the approved template
  // repeated inline — but delivered as a single WhatsApp message.
  const na = (v) => {
    const s = String(v ?? '').trim();
    return s || 'N/A';
  };
  const merged = new Map();
  selectedJobs.forEach((job) => {
    const key = [
      job.site_id || '',
      job.assigned_date || '',
      job.dumping_ground_id || '',
      (job.notes || '').trim(),
    ].join('|');
    if (merged.has(key)) {
      const m = merged.get(key);
      m.loads_required = (m.loads_required || 0) + (job.loads_required || 0);
    } else {
      merged.set(key, { ...job });
    }
  });
  const jobs = Array.from(merged.values()).sort((a, b) => (
    ((a.assigned_date || '') + (a.site_name || '')).localeCompare((b.assigned_date || '') + (b.site_name || ''))
  ));
  if (jobs.length === 0) return `Hi ${driverName}, no jobs assigned.\n\n— Unity Delivery Tracker`;
  const blocks = jobs.map((job) => (
    `Hi ${driverName}, here is your job for ${na(job.assigned_date)}:\n\n` +
    `Site: ${na(job.site_name)}\n` +
    `Company: ${na(job.customer_name)}\n` +
    `Loads: ${na(job.loads_required || 1)}\n` +
    `Unload: ${na(job.dumping_ground_name)}\n` +
    `Remarks: ${na(job.notes)}\n\n` +
    `— Unity Delivery Tracker`
  ));
  return blocks.join('\n\n');
}

export default function JobsTab({
  // Data
  jobs,
  unfinishedJobs,
  drivers,
  sites,
  filteredJobs,
  expiredJobsCount,
  archivedJobsCount = 0,
  // State
  showAddJob,
  setShowAddJob,
  newJob,
  setNewJob,
  jobFilter,
  setJobFilter,
  editingJob,
  setEditingJob,
  // Handlers
  handleCreateJob,
  handleDeleteJob,
  handleUpdateJobStatus,
  handleEditJob,
  handleSaveEditJob,
  handleArchiveJob,
  handleUnarchiveJob,
  handleArchiveAllExpired,
  fetchArchivedJobs,
}) {
  // WhatsApp multi-select state — supports selection across multiple drivers.
  const [selectedJobIds, setSelectedJobIds] = useState(() => new Set());
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  // Twilio send state: per-driver status keyed by driver_id
  // values: 'sending' | 'queued' | 'error'
  const [sendStatus, setSendStatus] = useState({});
  const [sendErrors, setSendErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [waConfig, setWaConfig] = useState({ configured: false, mode: 'sandbox', join_code: '', sender: '' });

  // ── Bulk-assign-by-site state ───────────────────────────────────────────
  const todaySgt = useMemo(() => getTomorrowSGT(), []); // reuse SGT helper (gives a YYYY-MM-DD string for date input default)
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [dumpingGrounds, setDumpingGrounds] = useState([]);
  useEffect(() => {
    // Load dumping grounds once so the optional selector in Assign New Job
    // and Assign Site to Multiple Drivers has data.
    axios.get(`${API}/dumping-grounds`).then(r => setDumpingGrounds(r.data || [])).catch(() => {});
  }, []);
  const emptyBulkRow = () => ({ key: Math.random().toString(36).slice(2), driver_id: '', driverSearch: '', loads_required: 4, notes: '' });
  const [bulkForm, setBulkForm] = useState({
    site_id: '',
    siteSearch: '',
    assigned_date: todaySgt,
    rows: [emptyBulkRow()],
    dumping_ground_id: '',
  });
  const [bulkSaving, setBulkSaving] = useState(false);

  const resetBulkForm = () => setBulkForm({
    site_id: '',
    siteSearch: '',
    assigned_date: todaySgt,
    rows: [emptyBulkRow()],
    dumping_ground_id: '',
  });

  const addBulkRow = () => setBulkForm(prev => ({ ...prev, rows: [...prev.rows, emptyBulkRow()] }));
  const removeBulkRow = (key) => setBulkForm(prev => ({
    ...prev,
    rows: prev.rows.length > 1 ? prev.rows.filter(r => r.key !== key) : prev.rows,
  }));
  const updateBulkRow = (key, patch) => setBulkForm(prev => ({
    ...prev,
    rows: prev.rows.map(r => r.key === key ? { ...r, ...patch } : r),
  }));

  const handleSubmitBulkAssign = async (e) => {
    e?.preventDefault?.();
    if (!bulkForm.site_id) {
      toast.error('Please select a site');
      return;
    }
    if (!bulkForm.assigned_date) {
      toast.error('Please select a date');
      return;
    }
    const drivers = bulkForm.rows
      .filter(r => r.driver_id)
      .map(r => ({
        driver_id: r.driver_id,
        loads_required: parseInt(r.loads_required, 10) || 1,
        notes: (r.notes || '').trim() || null,
      }));
    if (drivers.length === 0) {
      toast.error('Please add at least one driver');
      return;
    }
    // Reject duplicate drivers in the same submission
    const dupCheck = new Set();
    for (const d of drivers) {
      if (dupCheck.has(d.driver_id)) {
        toast.error('Each driver can only appear once');
        return;
      }
      dupCheck.add(d.driver_id);
    }
    setBulkSaving(true);
    try {
      const res = await axios.post(`${API}/admin/jobs/bulk`, {
        site_id: bulkForm.site_id,
        assigned_date: bulkForm.assigned_date,
        drivers,
        ...(bulkForm.dumping_ground_id ? { dumping_ground_id: bulkForm.dumping_ground_id } : {}),
      });
      const created = res.data?.created || [];
      const skipped = res.data?.skipped || [];
      if (created.length === 0) {
        toast.error('No jobs were created');
        return;
      }
      toast.success(`${created.length} job${created.length !== 1 ? 's' : ''} created${skipped.length ? `, ${skipped.length} skipped` : ''}`);
      setShowBulkAssign(false);
      resetBulkForm();
      // Refresh job lists in parent (handleCreateJob does this via fetchData — emulate)
      // The created jobs aren't in local `jobs` yet, so we pre-merge them temporarily into selection state.
      // Inject the created jobs so they can be selected for WhatsApp immediately,
      // even before the parent's polling refresh.
      const ids = created.map(j => j.id);
      // Stash full job objects so WhatsApp modal can render them
      setLocalCreatedJobs(created);
      setSelectedJobIds(new Set(ids));
      setShowWhatsAppModal(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create jobs');
    } finally {
      setBulkSaving(false);
    }
  };

  // Locally-cached jobs created via the bulk flow so the WhatsApp modal can
  // render them before the parent's `jobs` array is refreshed by polling.
  const [localCreatedJobs, setLocalCreatedJobs] = useState([]);

  // Load Twilio config once
  useEffect(() => {
    axios.get(`${API}/whatsapp/config`)
      .then(res => setWaConfig(res.data))
      .catch(() => setWaConfig({ configured: false, mode: 'sandbox', join_code: '', sender: '' }));
  }, []);

  const selectedJobs = useMemo(() => {
    if (selectedJobIds.size === 0) return [];
    // Merge parent jobs + locally-cached bulk-created jobs so the modal works
    // immediately after bulk-assign before polling refreshes the parent state.
    const map = new Map(jobs.map(j => [j.id, j]));
    for (const j of localCreatedJobs) {
      if (!map.has(j.id)) map.set(j.id, j);
    }
    return Array.from(selectedJobIds).map(id => map.get(id)).filter(Boolean);
  }, [selectedJobIds, jobs, localCreatedJobs]);

  // Group selected jobs by driver_id with driver record + sanitized phone.
  const selectedByDriver = useMemo(() => {
    const driverMap = new Map(drivers.map(d => [d.id, d]));
    const groups = new Map();
    for (const job of selectedJobs) {
      if (!groups.has(job.driver_id)) {
        const d = driverMap.get(job.driver_id);
        groups.set(job.driver_id, {
          driver_id: job.driver_id,
          driver_name: job.driver_name,
          phone: d?.phone || '',
          phoneSanitized: sanitizePhone(d?.phone || ''),
          jobs: []
        });
      }
      groups.get(job.driver_id).jobs.push(job);
    }
    return Array.from(groups.values());
  }, [selectedJobs, drivers]);

  // One WhatsApp message per driver (all their selected jobs are consolidated
  // into a single free-form message that preserves line breaks).
  const totalMessageCount = selectedByDriver.length;

  const toggleJobSelection = (job) => {
    setSelectedJobIds(prev => {
      const next = new Set(prev);
      if (next.has(job.id)) {
        next.delete(job.id);
      } else {
        next.add(job.id);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedJobIds(new Set());
    setSendStatus({});
    setSendErrors({});
  };

  const openModal = () => {
    if (selectedJobs.length === 0) {
      toast.error('No jobs selected');
      return;
    }
    setSendStatus({});
    setSendErrors({});
    setShowWhatsAppModal(true);
  };

  // Fire all messages in parallel via the backend Twilio endpoint, then poll
  // Twilio for each message's real delivery status (delivered / failed / …).
  const sendAllViaTwilio = async () => {
    if (selectedJobs.length === 0) return;
    // Mark all driver rows as 'sending'
    const initial = {};
    selectedByDriver.forEach(g => { initial[g.driver_id] = 'sending'; });
    setSendStatus(initial);
    setSendErrors({});
    setIsSending(true);
    try {
      const res = await axios.post(`${API}/whatsapp/send-jobs`, {
        job_ids: Array.from(selectedJobIds),
      });
      const nextStatus = {};
      const nextErrors = {};
      const sidToDriver = {}; // sid -> driver_id, for status polling
      for (const r of res.data.results || []) {
        if (r.status === 'queued') {
          nextStatus[r.driver_id] = 'queued';
          if (r.sid) sidToDriver[r.sid] = r.driver_id;
        } else {
          nextStatus[r.driver_id] = 'error';
          nextErrors[r.driver_id] = r.error || 'Failed';
        }
      }
      setSendStatus(nextStatus);
      setSendErrors(nextErrors);
      if (res.data.failed > 0) {
        toast.warning(`Sent ${res.data.sent}/${res.data.total}. ${res.data.failed} failed.`);
      } else {
        toast.success(`Queued ${res.data.sent} — verifying delivery…`);
      }
      // Poll Twilio for real delivery status of every queued SID. Meta delivery
      // takes 1-15s typically. We poll a few times so the admin sees whether
      // "queued" actually converts to "delivered/read" or bounces to "failed".
      const sids = Object.keys(sidToDriver);
      if (sids.length > 0) {
        const pollOnce = async () => {
          try {
            const st = await axios.post(`${API}/whatsapp/message-status`, { sids });
            const updates = {};
            const updateErrs = {};
            for (const m of st.data.results || []) {
              const did = sidToDriver[m.sid];
              if (!did) continue;
              const s = (m.status || '').toLowerCase();
              if (s === 'delivered' || s === 'read') {
                updates[did] = 'delivered';
              } else if (s === 'failed' || s === 'undelivered') {
                updates[did] = 'error';
                const code = m.error_code ? ` [${m.error_code}]` : '';
                updateErrs[did] = `${m.error_message || 'Not delivered by Meta'}${code}`;
              } else if (s === 'sent') {
                updates[did] = 'sent'; // handed off to WhatsApp, awaiting delivery
              } else if (s) {
                updates[did] = 'queued';
              }
            }
            setSendStatus(prev => ({ ...prev, ...updates }));
            setSendErrors(prev => ({ ...prev, ...updateErrs }));
            return st.data.results || [];
          } catch {
            return [];
          }
        };
        // Poll now, then at 3s, 8s, 15s
        for (const delay of [500, 3000, 5000, 7000]) {
          await new Promise(r => setTimeout(r, delay));
          const rows = await pollOnce();
          const allFinal = rows.every(r => ['delivered', 'read', 'failed', 'undelivered'].includes((r.status || '').toLowerCase()));
          if (allFinal) break;
        }
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to send';
      toast.error(`Send failed: ${msg}`);
      // Reset all to error
      const errStatus = {};
      const errErrors = {};
      selectedByDriver.forEach(g => {
        errStatus[g.driver_id] = 'error';
        errErrors[g.driver_id] = msg;
      });
      setSendStatus(errStatus);
      setSendErrors(errErrors);
    } finally {
      setIsSending(false);
    }
  };

  const openManualWaMe = (group) => {
    if (!group.phoneSanitized) {
      toast.error('No phone number on file');
      return;
    }
    const message = buildWhatsAppMessage(group.driver_name, group.jobs);
    const url = `https://wa.me/${group.phoneSanitized}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const deliveredCount = Object.values(sendStatus).filter(s => s === 'delivered').length;
  const sentCount = Object.values(sendStatus).filter(s => s === 'queued' || s === 'sent' || s === 'delivered').length;
  const errorCount = Object.values(sendStatus).filter(s => s === 'error').length;

  return (
    <div className="space-y-4">
      {/* Sticky WhatsApp action bar */}
      {selectedJobIds.size > 0 && (
        <div
          className="sticky top-0 z-30 bg-[#0d4f3c] border border-green-500/40 p-3 flex flex-wrap items-center gap-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.4)]"
          data-testid="whatsapp-action-bar"
        >
          <MessageCircle className="w-5 h-5 text-green-300" strokeWidth={2} />
          <div className="flex-1 min-w-[200px]">
            <p className="text-white font-heading font-bold text-sm uppercase">
              {selectedJobIds.size} JOB{selectedJobIds.size !== 1 ? 'S' : ''} SELECTED
              {selectedByDriver.length > 1 && (
                <span className="ml-2 text-green-200">
                  · {selectedByDriver.length} DRIVERS
                </span>
              )}
            </p>
            <p className="text-xs text-green-200">
              {selectedByDriver.length === 1
                ? `Driver: ${selectedByDriver[0].driver_name}${selectedByDriver[0].phone ? ` · ${selectedByDriver[0].phone}` : ' · No WhatsApp number on file'}`
                : selectedByDriver.map(g => g.driver_name).join(', ')}
            </p>
          </div>
          <button
            onClick={openModal}
            className="px-4 h-10 bg-green-600 hover:bg-green-500 text-white font-heading font-bold text-xs uppercase flex items-center gap-2"
            data-testid="send-whatsapp-button"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2} />
            SEND TO WHATSAPP
          </button>
          <button
            onClick={clearSelection}
            className="px-4 h-10 bg-transparent border border-white/30 text-white/80 font-heading font-bold text-xs uppercase hover:border-white/60"
            data-testid="clear-job-selection"
          >
            CLEAR
          </button>
        </div>
      )}

      {/* WhatsApp confirmation modal — Twilio automated send + manual fallback */}
      {showWhatsAppModal && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          data-testid="whatsapp-modal"
        >
          <div className="bg-[#161920] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b border-white/10 sticky top-0 bg-[#161920] z-10">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-400" strokeWidth={2} />
                <h3 className="font-heading font-bold text-lg uppercase text-white">
                  Send to WhatsApp · {selectedByDriver.length} driver{selectedByDriver.length !== 1 ? 's' : ''}
                </h3>
              </div>
              <button
                onClick={() => setShowWhatsAppModal(false)}
                className="text-white/40 hover:text-white"
                data-testid="close-whatsapp-modal"
              >
                <X className="w-5 h-5" strokeWidth={2} />
              </button>
            </div>

            <div className="p-5 space-y-3">
              {/* Twilio status banner — production vs sandbox vs not configured */}
              {!waConfig.configured ? (
                <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 text-xs text-yellow-200 flex gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <p>Twilio not configured on server. Use the per-driver manual link below.</p>
                </div>
              ) : waConfig.mode === 'production' ? (
                waConfig.template_status === 'approved' ? (
                  <div className="bg-green-500/10 border border-green-500/30 p-3 text-xs text-green-200 flex gap-2">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div className="flex-1">
                      <p className="font-bold mb-1">Production WhatsApp · Live (template approved)</p>
                      <p>
                        Messages sent from <span className="font-mono">+{(waConfig.sender || '').replace(/^\+/, '')}</span> via approved template. No driver opt-in required.
                      </p>
                      <details className="mt-2">
                        <summary className="cursor-pointer text-green-300/80 hover:text-green-100">Show template details</summary>
                        <div className="mt-2 space-y-1 font-mono text-[11px] text-green-100/80" data-testid="wa-template-details">
                          <p>Content SID: <span className="text-green-100 select-all">{waConfig.content_sid || '—'}</span></p>
                          <p>Language: <span className="text-green-100">{waConfig.template_language || '(unknown)'}</span></p>
                          <p>Category: <span className="text-green-100">{waConfig.template_category || '(unknown)'}</span></p>
                          <p>Approved WABAs: <span className="text-green-100">{(waConfig.template_waba_ids && waConfig.template_waba_ids.length) ? waConfig.template_waba_ids.join(', ') : '(not exposed by Twilio API)'}</span></p>
                        </div>
                        <p className="mt-2 text-green-200/70 leading-relaxed">
                          If sends fail with <span className="font-mono">63027</span>, the template above isn't approved for the sender's WABA or recipient's locale. Verify this Content SID in Twilio Console → Content Template Builder and confirm it's approved under the WABA that owns +{(waConfig.sender || '').replace(/^\+/, '')}.
                        </p>
                      </details>
                    </div>
                  </div>
                ) : waConfig.template_status === 'pending' ? (
                  <div className="bg-orange-500/10 border border-orange-500/30 p-3 text-xs text-orange-200 flex gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div>
                      <p className="font-bold mb-1">Template pending Meta approval</p>
                      <p>
                        Your WhatsApp message template is still under review (typically 15min–24h). Until approved, sends may queue but won't be delivered (error 63016). Check status at <span className="font-mono text-orange-100">Twilio Console → Content Builder</span>.
                      </p>
                    </div>
                  </div>
                ) : waConfig.template_status === 'rejected' ? (
                  <div className="bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-200 flex gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <p>Template was rejected by Meta. Fix and resubmit in Twilio Console → Content Builder.</p>
                  </div>
                ) : (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 text-xs text-yellow-200 flex gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div>
                      <p className="font-bold mb-1">Production sender configured, but no approved template</p>
                      <p>
                        Free-form messages to drivers outside a 24h window will be rejected by Meta (error 63016). Submit a message template via Twilio Console for reliable delivery.
                      </p>
                    </div>
                  </div>
                )
              ) : (
                <div className="bg-blue-500/10 border border-blue-500/30 p-3 text-xs text-blue-200 flex gap-2">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <div>
                    <p className="font-bold mb-1">Twilio Sandbox active</p>
                    <p>
                      Each driver must send <span className="font-mono bg-black/40 px-1.5 py-0.5">{waConfig.join_code || 'join <code>'}</span> from their WhatsApp to <span className="font-mono">+{(waConfig.sender || '').replace(/^\+/, '')}</span> <span className="font-bold">once</span> before they can receive messages.
                    </p>
                  </div>
                </div>
              )}

              {/* Driver rows with live status */}
              {selectedByDriver.map((group) => {
                const status = sendStatus[group.driver_id]; // undefined | 'sending' | 'queued' | 'error'
                const errMsg = sendErrors[group.driver_id];
                const hasPhone = !!group.phoneSanitized;
                return (
                  <div
                    key={group.driver_id}
                    className={`border p-4 ${
                      status === 'delivered' ? 'border-green-500/40 bg-green-500/5'
                      : status === 'sent' ? 'border-blue-500/40 bg-blue-500/5'
                      : status === 'queued' ? 'border-yellow-500/40 bg-yellow-500/5'
                      : status === 'error' ? 'border-red-500/40 bg-red-500/5'
                      : 'border-white/10 bg-[#1a1d24]'
                    }`}
                    data-testid={`wa-driver-row-${group.driver_id}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[180px]">
                        <p className="font-heading font-bold text-white uppercase text-sm">{group.driver_name}</p>
                        <p className="text-xs text-white/50 font-mono">
                          {hasPhone ? `+${group.phoneSanitized}` : 'No WhatsApp number on file'}
                        </p>
                        <p className="text-xs text-primary mt-1">
                          {group.jobs.length} job{group.jobs.length !== 1 ? 's' : ''}
                        </p>
                        {/* Status pill */}
                        {status === 'sending' && (
                          <p className="text-xs text-blue-300 mt-1 flex items-center gap-1" data-testid={`wa-status-${group.driver_id}`}>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} /> Sending…
                          </p>
                        )}
                        {status === 'queued' && (
                          <p className="text-xs text-yellow-300 mt-1 flex items-center gap-1" data-testid={`wa-status-${group.driver_id}`} title="Twilio accepted the message. Awaiting Meta delivery.">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} /> Queued (waiting for Meta)…
                          </p>
                        )}
                        {status === 'sent' && (
                          <p className="text-xs text-blue-300 mt-1 flex items-center gap-1" data-testid={`wa-status-${group.driver_id}`} title="Handed off to WhatsApp; not yet confirmed delivered.">
                            <CheckCircle className="w-3.5 h-3.5" strokeWidth={2} /> Sent to WhatsApp
                          </p>
                        )}
                        {status === 'delivered' && (
                          <p className="text-xs text-green-300 mt-1 flex items-center gap-1" data-testid={`wa-status-${group.driver_id}`}>
                            <CheckCircle className="w-3.5 h-3.5" strokeWidth={2} /> Delivered
                          </p>
                        )}
                        {status === 'error' && (
                          <p className="text-xs text-red-300 mt-1 flex items-center gap-1" data-testid={`wa-status-${group.driver_id}`} title={errMsg}>
                            <XCircle className="w-3.5 h-3.5" strokeWidth={2} /> Failed: {errMsg}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        <button
                          onClick={() => openManualWaMe(group)}
                          disabled={!hasPhone}
                          className={`px-3 h-8 text-xs uppercase font-heading font-bold flex items-center gap-1.5 border ${
                            hasPhone
                              ? 'border-white/20 text-white/70 hover:border-white/40 hover:text-white'
                              : 'border-white/5 text-white/20 cursor-not-allowed'
                          }`}
                          data-testid={`open-manual-${group.driver_id}`}
                          title="Open WhatsApp manually (wa.me link)"
                        >
                          <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} />
                          MANUAL
                        </button>
                      </div>
                    </div>
                    <details className="mt-2">
                      <summary className="text-xs text-white/40 cursor-pointer hover:text-white/60">Preview message</summary>
                      <pre className="mt-2 p-2 bg-black/30 text-xs text-white/70 whitespace-pre-wrap font-mono">
{buildWhatsAppMessage(group.driver_name, group.jobs)}
                      </pre>
                    </details>
                  </div>
                );
              })}
            </div>

            <div className="p-4 sm:p-5 border-t border-white/10 sticky bottom-0 bg-[#161920] z-10 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3">
              <p className="text-xs text-white/50 sm:flex-1 sm:pr-4">
                {deliveredCount > 0 && <span className="text-green-300">{deliveredCount} delivered</span>}
                {sentCount > deliveredCount && (
                  <>
                    {deliveredCount > 0 && <span className="mx-1">·</span>}
                    <span className="text-yellow-300">{sentCount - deliveredCount} awaiting Meta</span>
                  </>
                )}
                {errorCount > 0 && <span className="mx-1">·</span>}
                {errorCount > 0 && <span className="text-red-300">{errorCount} failed</span>}
              {sentCount === 0 && errorCount === 0 && (
                <span>
                  Ready to send {totalMessageCount} WhatsApp message{totalMessageCount !== 1 ? 's' : ''}
                  {selectedByDriver.length > 0 && totalMessageCount > selectedByDriver.length && (
                    <span className="text-white/40"> · {selectedByDriver.length} driver{selectedByDriver.length !== 1 ? 's' : ''}, {totalMessageCount} separate message{totalMessageCount !== 1 ? 's' : ''}</span>
                  )}
                </span>
              )}
              </p>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setShowWhatsAppModal(false)}
                  className="flex-1 sm:flex-none px-4 h-10 bg-transparent border border-white/20 text-white/80 font-heading font-bold text-xs uppercase hover:border-white/40 whitespace-nowrap"
                  data-testid="close-whatsapp-modal-bottom"
                >
                  CLOSE
                </button>
                <button
                  onClick={sendAllViaTwilio}
                  disabled={isSending || !waConfig.configured}
                  className={`flex-1 sm:flex-none px-5 h-10 font-heading font-bold text-xs uppercase flex items-center justify-center gap-2 whitespace-nowrap ${
                    waConfig.configured && !isSending
                      ? 'bg-green-600 hover:bg-green-500 text-white'
                      : 'bg-white/5 text-white/40 cursor-not-allowed'
                  }`}
                  data-testid="send-all-twilio-button"
                >
                  {isSending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                      SENDING…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" strokeWidth={2} />
                      <span className="sm:hidden">SEND ALL</span>
                      <span className="hidden sm:inline">SEND ALL VIA TWILIO</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

      )}

      {/* Bulk Assign Modal — assign one site to multiple drivers with per-driver loads + remarks */}
      {showBulkAssign && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          data-testid="bulk-assign-modal"
        >
          <form
            onSubmit={handleSubmitBulkAssign}
            className="bg-[#161920] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center p-5 border-b border-white/10 sticky top-0 bg-[#161920] z-10">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" strokeWidth={2} />
                <h3 className="font-heading font-bold text-lg uppercase text-white">
                  Assign Site to Multiple Drivers
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowBulkAssign(false)}
                className="text-white/40 hover:text-white"
                data-testid="close-bulk-assign-modal"
              >
                <X className="w-5 h-5" strokeWidth={2} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Site + Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-xs font-semibold text-white/60 uppercase mb-2">Site *</label>
                  <input
                    type="text"
                    placeholder="Search site by name or customer..."
                    value={bulkForm.siteSearch}
                    onChange={(e) => setBulkForm(prev => ({ ...prev, siteSearch: e.target.value, site_id: '' }))}
                    className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                    data-testid="bulk-site-search"
                  />
                  {bulkForm.siteSearch && !bulkForm.site_id && (
                    <div className="absolute z-20 w-full mt-1 max-h-48 overflow-y-auto bg-[#1a1d24] border border-white/20">
                      {sites
                        .filter(s =>
                          s.name.toLowerCase().includes(bulkForm.siteSearch.toLowerCase()) ||
                          (s.customer_name || '').toLowerCase().includes(bulkForm.siteSearch.toLowerCase())
                        )
                        .slice(0, 12)
                        .map(s => (
                          <div
                            key={s.id}
                            onClick={() => setBulkForm(prev => ({ ...prev, site_id: s.id, siteSearch: `${s.name} (${s.customer_name || 'No customer'})` }))}
                            className="px-3 py-2 hover:bg-white/10 cursor-pointer text-white text-sm"
                          >
                            <span className="font-semibold">{s.name}</span>
                            <span className="text-white/60 ml-2">({s.customer_name || 'No customer'})</span>
                          </div>
                        ))
                      }
                    </div>
                  )}
                  {bulkForm.site_id && (
                    <button
                      type="button"
                      onClick={() => setBulkForm(prev => ({ ...prev, site_id: '', siteSearch: '' }))}
                      className="absolute right-2 top-9 text-white/40 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/60 uppercase mb-2">Date *</label>
                  <input
                    type="date"
                    value={bulkForm.assigned_date}
                    onChange={(e) => setBulkForm(prev => ({ ...prev, assigned_date: e.target.value }))}
                    className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm [color-scheme:dark]"
                    required
                    data-testid="bulk-assigned-date"
                  />
                </div>
              </div>

              {/* Dumping Ground (optional) */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase mb-2">Dumping Ground (optional)</label>
                <select
                  value={bulkForm.dumping_ground_id}
                  onChange={(e) => setBulkForm(prev => ({ ...prev, dumping_ground_id: e.target.value }))}
                  className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                  data-testid="bulk-dumping-ground"
                >
                  <option value="">— None —</option>
                  {dumpingGrounds.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>

              {/* Driver rows */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-heading font-bold uppercase text-white/80">Drivers ({bulkForm.rows.length})</p>
                  <button
                    type="button"
                    onClick={addBulkRow}
                    className="flex items-center gap-1 px-3 h-8 bg-white/5 hover:bg-white/10 border border-white/20 text-white text-xs font-heading font-bold uppercase"
                    data-testid="bulk-add-driver"
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Add Driver
                  </button>
                </div>
                {bulkForm.rows.map((row, idx) => {
                  const matches = drivers.filter(d =>
                    !row.driverSearch ||
                    d.full_name.toLowerCase().includes(row.driverSearch.toLowerCase()) ||
                    (d.truck_number || '').toLowerCase().includes(row.driverSearch.toLowerCase())
                  ).slice(0, 10);
                  return (
                    <div
                      key={row.key}
                      className="bg-[#1a1d24] border border-white/10 p-3 grid grid-cols-12 gap-2 items-start"
                      data-testid={`bulk-row-${idx}`}
                    >
                      {/* Driver picker */}
                      <div className="col-span-12 md:col-span-5 relative">
                        <label className="block text-[10px] uppercase text-white/40 mb-1">Driver *</label>
                        <input
                          type="text"
                          placeholder="Search driver..."
                          value={row.driverSearch}
                          onChange={(e) => updateBulkRow(row.key, { driverSearch: e.target.value, driver_id: '' })}
                          className="w-full h-9 px-2 bg-[#0f1218] border border-white/15 focus:border-primary focus:outline-none text-white text-sm"
                          data-testid={`bulk-driver-search-${idx}`}
                        />
                        {row.driverSearch && !row.driver_id && (
                          <div className="absolute z-20 w-full mt-1 max-h-44 overflow-y-auto bg-[#0f1218] border border-white/15">
                            {matches.map(d => (
                              <div
                                key={d.id}
                                onClick={() => updateBulkRow(row.key, {
                                  driver_id: d.id,
                                  driverSearch: `${d.full_name} (${d.truck_number || 'N/A'})`,
                                })}
                                className="px-2 py-1.5 hover:bg-white/10 cursor-pointer text-white text-xs"
                              >
                                <span className="font-semibold">{d.full_name}</span>
                                <span className="text-white/50 ml-1">({d.truck_number || 'N/A'})</span>
                                {!d.phone && <span className="text-yellow-400 ml-1">· no WA</span>}
                              </div>
                            ))}
                            {matches.length === 0 && (
                              <div className="px-2 py-1.5 text-white/40 text-xs">No drivers found</div>
                            )}
                          </div>
                        )}
                        {row.driver_id && (
                          <button
                            type="button"
                            onClick={() => updateBulkRow(row.key, { driver_id: '', driverSearch: '' })}
                            className="absolute right-2 top-7 text-white/40 hover:text-white"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      {/* Loads */}
                      <div className="col-span-4 md:col-span-2">
                        <label className="block text-[10px] uppercase text-white/40 mb-1">Loads</label>
                        <input
                          type="number"
                          min="1"
                          value={row.loads_required}
                          onChange={(e) => updateBulkRow(row.key, { loads_required: e.target.value })}
                          className="w-full h-9 px-2 bg-[#0f1218] border border-white/15 focus:border-primary focus:outline-none text-white text-sm"
                          data-testid={`bulk-loads-${idx}`}
                        />
                      </div>
                      {/* Remarks */}
                      <div className="col-span-7 md:col-span-4">
                        <label className="block text-[10px] uppercase text-white/40 mb-1">Remarks</label>
                        <input
                          type="text"
                          placeholder="Optional notes for this driver…"
                          value={row.notes}
                          onChange={(e) => updateBulkRow(row.key, { notes: e.target.value })}
                          className="w-full h-9 px-2 bg-[#0f1218] border border-white/15 focus:border-primary focus:outline-none text-white text-sm"
                          data-testid={`bulk-notes-${idx}`}
                        />
                      </div>
                      {/* Remove */}
                      <div className="col-span-1 flex items-end justify-end h-full">
                        <button
                          type="button"
                          onClick={() => removeBulkRow(row.key)}
                          disabled={bulkForm.rows.length === 1}
                          className={`mt-5 p-2 ${bulkForm.rows.length === 1 ? 'text-white/20 cursor-not-allowed' : 'text-red-400 hover:bg-red-500/10'}`}
                          title="Remove driver"
                          data-testid={`bulk-remove-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-4 sm:p-5 border-t border-white/10 sticky bottom-0 bg-[#161920] z-10 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3">
              <p className="text-xs text-white/50 hidden sm:block sm:flex-1 sm:pr-4">
                After creating, the WhatsApp send modal opens automatically with all new jobs pre-selected.
              </p>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setShowBulkAssign(false)}
                  className="flex-1 sm:flex-none px-4 h-10 bg-transparent border border-white/20 text-white/80 font-heading font-bold text-xs uppercase hover:border-white/40 whitespace-nowrap"
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  disabled={bulkSaving}
                  className={`flex-1 sm:flex-none px-5 h-10 font-heading font-bold text-xs uppercase flex items-center justify-center gap-2 whitespace-nowrap ${
                    bulkSaving ? 'bg-white/5 text-white/40 cursor-not-allowed' : 'bg-primary hover:bg-primary/80 text-white'
                  }`}
                  data-testid="bulk-assign-submit"
                >
                  {bulkSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> CREATING…
                    </>
                  ) : (
                    <>
                      <Briefcase className="w-4 h-4" strokeWidth={2} />
                      <span className="sm:hidden">CREATE & SEND</span>
                      <span className="hidden sm:inline">CREATE & SEND VIA WHATSAPP</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Jobs Header with Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#161920] border border-white/10 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-white/60 uppercase">Unfinished Jobs</p>
              <p className="text-2xl font-bold text-yellow-400">{unfinishedJobs.total_unfinished}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-yellow-400" strokeWidth={2} />
          </div>
        </div>
        <div className="bg-[#161920] border border-white/10 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-white/60 uppercase">Pending</p>
              <p className="text-2xl font-bold text-blue-400">{jobs.filter(j => j.status === 'pending').length}</p>
            </div>
            <Clock className="w-8 h-8 text-blue-400" strokeWidth={2} />
          </div>
        </div>
        <div className="bg-[#161920] border border-white/10 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-white/60 uppercase">Completed Today</p>
              <p className="text-2xl font-bold text-green-400">
                {jobs.filter(j => j.status === 'completed' && j.assigned_date === new Date().toISOString().split('T')[0]).length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" strokeWidth={2} />
          </div>
        </div>
      </div>

      {/* Create Job Buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setShowAddJob(!showAddJob)}
          className="flex items-center gap-2 px-6 h-12 bg-primary text-white font-heading font-bold uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
          data-testid="create-job-button"
        >
          <Briefcase className="w-5 h-5" strokeWidth={2} />
          {showAddJob ? "CANCEL" : "ASSIGN NEW JOB"}
        </button>
        <button
          onClick={() => { resetBulkForm(); setShowBulkAssign(true); }}
          className="flex items-center gap-2 px-6 h-12 bg-[#1d4ed8] hover:bg-[#1e40af] text-white font-heading font-bold uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
          data-testid="bulk-assign-button"
        >
          <Users className="w-5 h-5" strokeWidth={2} />
          ASSIGN SITE TO MULTIPLE DRIVERS
        </button>
      </div>

      {/* Create Job Form */}
      {showAddJob && (
        <div className="bg-[#161920] border border-white/10 p-6">
          <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">ASSIGN JOB</h3>
          <form onSubmit={handleCreateJob} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Driver Search */}
              <div>
                <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Driver</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search driver by name or vehicle..."
                    value={newJob.driverSearch || ''}
                    onChange={(e) => setNewJob({ ...newJob, driverSearch: e.target.value, driver_id: '' })}
                    className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                    data-testid="new-job-driver-search"
                  />
                  {newJob.driverSearch && !newJob.driver_id && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1d24] border border-white/20 max-h-48 overflow-y-auto z-50">
                      {drivers
                        .filter(d => 
                          d.full_name.toLowerCase().includes((newJob.driverSearch || '').toLowerCase()) ||
                          (d.truck_number || '').toLowerCase().includes((newJob.driverSearch || '').toLowerCase())
                        )
                        .map(driver => (
                          <div
                            key={driver.id}
                            onClick={() => setNewJob({ ...newJob, driver_id: driver.id, driverSearch: `${driver.full_name} (${driver.truck_number || 'No vehicle'})` })}
                            className="px-3 py-2 hover:bg-white/10 cursor-pointer text-white text-sm"
                          >
                            <span className="font-semibold">{driver.full_name}</span>
                            <span className="text-white/60 ml-2">({driver.truck_number || 'No vehicle'})</span>
                          </div>
                        ))
                      }
                      {drivers.filter(d => 
                        d.full_name.toLowerCase().includes((newJob.driverSearch || '').toLowerCase()) ||
                        (d.truck_number || '').toLowerCase().includes((newJob.driverSearch || '').toLowerCase())
                      ).length === 0 && (
                        <div className="px-3 py-2 text-white/40 text-sm">No drivers found</div>
                      )}
                    </div>
                  )}
                  {newJob.driver_id && (
                    <button
                      type="button"
                      onClick={() => setNewJob({ ...newJob, driver_id: '', driverSearch: '' })}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                    >
                      <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                  )}
                </div>
              </div>

              {/* Site Search */}
              <div>
                <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Site</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search site by name or customer..."
                    value={newJob.siteSearch || ''}
                    onChange={(e) => setNewJob({ ...newJob, siteSearch: e.target.value, site_id: '' })}
                    className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                    data-testid="new-job-site-search"
                  />
                  {newJob.siteSearch && !newJob.site_id && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1d24] border border-white/20 max-h-48 overflow-y-auto z-50">
                      {sites
                        .filter(s => 
                          s.name.toLowerCase().includes((newJob.siteSearch || '').toLowerCase()) ||
                          (s.customer_name || '').toLowerCase().includes((newJob.siteSearch || '').toLowerCase())
                        )
                        .map(site => (
                          <div
                            key={site.id}
                            onClick={() => setNewJob({ ...newJob, site_id: site.id, siteSearch: `${site.name} (${site.customer_name || 'No customer'})` })}
                            className="px-3 py-2 hover:bg-white/10 cursor-pointer text-white text-sm"
                          >
                            <span className="font-semibold">{site.name}</span>
                            <span className="text-white/60 ml-2">({site.customer_name || 'No customer'})</span>
                          </div>
                        ))
                      }
                      {sites.filter(s => 
                        s.name.toLowerCase().includes((newJob.siteSearch || '').toLowerCase()) ||
                        (s.customer_name || '').toLowerCase().includes((newJob.siteSearch || '').toLowerCase())
                      ).length === 0 && (
                        <div className="px-3 py-2 text-white/40 text-sm">No sites found</div>
                      )}
                    </div>
                  )}
                  {newJob.site_id && (
                    <button
                      type="button"
                      onClick={() => setNewJob({ ...newJob, site_id: '', siteSearch: '' })}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                    >
                      <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                  )}
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Date</label>
                <input
                  type="date"
                  value={newJob.assigned_date}
                  onChange={(e) => setNewJob({ ...newJob, assigned_date: e.target.value })}
                  className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm [color-scheme:dark]"
                  required
                  data-testid="new-job-date"
                />
              </div>

              {/* Number of Loads */}
              <div>
                <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Number of Loads (optional)</label>
                <input
                  type="number"
                  min="1"
                  value={newJob.loads_required}
                  onChange={(e) => setNewJob({ ...newJob, loads_required: e.target.value })}
                  placeholder="Default: 1"
                  className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                  data-testid="new-job-loads"
                />
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Notes (optional)</label>
                <input
                  type="text"
                  value={newJob.notes}
                  onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })}
                  placeholder="Any special instructions..."
                  className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                  data-testid="new-job-notes"
                />
              </div>

              {/* Dumping Ground (optional) */}
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold font-body text-white/60 uppercase mb-2">Dumping Ground (optional)</label>
                <select
                  value={newJob.dumping_ground_id || ''}
                  onChange={(e) => setNewJob({ ...newJob, dumping_ground_id: e.target.value })}
                  className="w-full h-10 px-3 bg-[#1a1d24] border border-white/20 focus:border-primary focus:outline-none text-white text-sm"
                  data-testid="new-job-dumping-ground"
                >
                  <option value="">— None —</option>
                  {dumpingGrounds.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-6 h-10 bg-primary text-white font-heading font-bold text-sm uppercase"
                data-testid="submit-new-job"
              >
                ASSIGN JOB
              </button>
              <button
                type="button"
                onClick={() => setShowAddJob(false)}
                className="px-6 h-10 bg-transparent border border-white/20 text-white/60 font-heading font-bold text-sm uppercase hover:border-white/40"
              >
                CANCEL
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="edit-job-modal">
          <div className="bg-[#161920] border border-white/10 p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-heading font-bold text-white uppercase">Edit Job</h3>
              <button onClick={() => setEditingJob(null)} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Driver Search */}
              <div className="relative">
                <label className="block text-xs font-bold text-white/60 uppercase mb-2">Driver *</label>
                <input
                  type="text"
                  value={editingJob.driverSearch || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, driverSearch: e.target.value, driver_id: '' })}
                  placeholder="Search driver..."
                  className="w-full h-10 px-3 bg-[#1a1d24] border border-white/10 text-white text-sm focus:border-primary/50 focus:outline-none"
                  data-testid="edit-job-driver-search"
                />
                {editingJob.driverSearch && !editingJob.driver_id && (
                  <div className="absolute z-20 w-full mt-1 max-h-48 overflow-y-auto bg-[#1a1d24] border border-white/10">
                    {drivers
                      .filter(d => 
                        d.full_name.toLowerCase().includes(editingJob.driverSearch.toLowerCase()) ||
                        (d.truck_number && d.truck_number.toLowerCase().includes(editingJob.driverSearch.toLowerCase()))
                      )
                      .slice(0, 10)
                      .map(d => (
                        <div
                          key={d.id}
                          onClick={() => setEditingJob({
                            ...editingJob,
                            driver_id: d.id,
                            driverSearch: `${d.full_name} (${d.truck_number || 'N/A'})`
                          })}
                          className="px-3 py-2 text-sm text-white hover:bg-white/10 cursor-pointer"
                        >
                          {d.full_name} <span className="text-white/40">({d.truck_number || 'N/A'})</span>
                        </div>
                      ))
                    }
                    {drivers.filter(d => 
                      d.full_name.toLowerCase().includes(editingJob.driverSearch.toLowerCase()) ||
                      (d.truck_number && d.truck_number.toLowerCase().includes(editingJob.driverSearch.toLowerCase()))
                    ).length === 0 && (
                      <div className="px-3 py-2 text-white/40 text-sm">No drivers found</div>
                    )}
                  </div>
                )}
                {editingJob.driver_id && (
                  <button
                    type="button"
                    onClick={() => setEditingJob({ ...editingJob, driver_id: '', driverSearch: '' })}
                    className="absolute right-2 top-8 text-white/40 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Site Search */}
              <div className="relative">
                <label className="block text-xs font-bold text-white/60 uppercase mb-2">Site *</label>
                <input
                  type="text"
                  value={editingJob.siteSearch || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, siteSearch: e.target.value, site_id: '' })}
                  placeholder="Search site..."
                  className="w-full h-10 px-3 bg-[#1a1d24] border border-white/10 text-white text-sm focus:border-primary/50 focus:outline-none"
                  data-testid="edit-job-site-search"
                />
                {editingJob.siteSearch && !editingJob.site_id && (
                  <div className="absolute z-20 w-full mt-1 max-h-48 overflow-y-auto bg-[#1a1d24] border border-white/10">
                    {sites
                      .filter(s => 
                        s.name.toLowerCase().includes(editingJob.siteSearch.toLowerCase()) ||
                        (s.customer_name && s.customer_name.toLowerCase().includes(editingJob.siteSearch.toLowerCase()))
                      )
                      .slice(0, 10)
                      .map(s => (
                        <div
                          key={s.id}
                          onClick={() => setEditingJob({
                            ...editingJob,
                            site_id: s.id,
                            siteSearch: `${s.name} (${s.customer_name || 'N/A'})`
                          })}
                          className="px-3 py-2 text-sm text-white hover:bg-white/10 cursor-pointer"
                        >
                          {s.name} <span className="text-white/40">({s.customer_name || 'N/A'})</span>
                        </div>
                      ))
                    }
                    {sites.filter(s => 
                      s.name.toLowerCase().includes(editingJob.siteSearch.toLowerCase()) ||
                      (s.customer_name && s.customer_name.toLowerCase().includes(editingJob.siteSearch.toLowerCase()))
                    ).length === 0 && (
                      <div className="px-3 py-2 text-white/40 text-sm">No sites found</div>
                    )}
                  </div>
                )}
                {editingJob.site_id && (
                  <button
                    type="button"
                    onClick={() => setEditingJob({ ...editingJob, site_id: '', siteSearch: '' })}
                    className="absolute right-2 top-8 text-white/40 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="block text-xs font-bold text-white/60 uppercase mb-2">Date *</label>
                <input
                  type="date"
                  value={editingJob.assigned_date}
                  onChange={(e) => setEditingJob({ ...editingJob, assigned_date: e.target.value })}
                  className="w-full h-10 px-3 bg-[#1a1d24] border border-white/10 text-white text-sm focus:border-primary/50 focus:outline-none [color-scheme:dark]"
                  data-testid="edit-job-date"
                />
              </div>

              {/* Number of Loads */}
              <div>
                <label className="block text-xs font-bold text-white/60 uppercase mb-2">Number of Loads</label>
                <input
                  type="number"
                  min="1"
                  value={editingJob.loads_required || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, loads_required: e.target.value })}
                  placeholder="Leave empty for 1"
                  className="w-full h-10 px-3 bg-[#1a1d24] border border-white/10 text-white text-sm focus:border-primary/50 focus:outline-none"
                  data-testid="edit-job-loads"
                />
                <p className="text-xs text-white/40 mt-1">Completed: {editingJob.loads_completed || 0} loads</p>
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-white/60 uppercase mb-2">Notes</label>
                <textarea
                  value={editingJob.notes || ''}
                  onChange={(e) => setEditingJob({ ...editingJob, notes: e.target.value })}
                  placeholder="Optional notes..."
                  rows={2}
                  className="w-full px-3 py-2 bg-[#1a1d24] border border-white/10 text-white text-sm focus:border-primary/50 focus:outline-none resize-none"
                  data-testid="edit-job-notes"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleSaveEditJob}
                className="px-6 h-10 bg-primary text-white font-heading font-bold text-sm uppercase"
                data-testid="save-edit-job"
              >
                SAVE CHANGES
              </button>
              <button
                onClick={() => setEditingJob(null)}
                className="px-6 h-10 bg-transparent border border-white/20 text-white/60 font-heading font-bold text-sm uppercase hover:border-white/40"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setJobFilter('unfinished')}
          className={`px-4 h-9 font-heading font-bold text-xs uppercase border ${
            jobFilter === 'unfinished' ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400' : 'bg-transparent border-white/20 text-white/60'
          }`}
        >
          Unfinished ({jobs.filter(j => j.status !== 'completed' && !isJobExpired(j)).length})
        </button>
        <button
          onClick={() => setJobFilter('completed')}
          className={`px-4 h-9 font-heading font-bold text-xs uppercase border ${
            jobFilter === 'completed' ? 'bg-green-500/20 border-green-500 text-green-400' : 'bg-transparent border-white/20 text-white/60'
          }`}
        >
          Completed ({jobs.filter(j => j.status === 'completed').length})
        </button>
        <button
          onClick={() => setJobFilter('expired')}
          className={`px-4 h-9 font-heading font-bold text-xs uppercase border ${
            jobFilter === 'expired' ? 'bg-red-500/20 border-red-500 text-red-400' : 'bg-transparent border-white/20 text-white/60'
          }`}
        >
          Expired ({expiredJobsCount})
        </button>
        <button
          onClick={() => { setJobFilter('archived'); fetchArchivedJobs && fetchArchivedJobs(); }}
          className={`px-4 h-9 font-heading font-bold text-xs uppercase border ${
            jobFilter === 'archived' ? 'bg-white/10 border-white/40 text-white' : 'bg-transparent border-white/20 text-white/60'
          }`}
          data-testid="archived-filter-btn"
        >
          Archived ({archivedJobsCount})
        </button>
        <button
          onClick={() => setJobFilter('all')}
          className={`px-4 h-9 font-heading font-bold text-xs uppercase border ${
            jobFilter === 'all' ? 'bg-primary/20 border-primary text-primary' : 'bg-transparent border-white/20 text-white/60'
          }`}
        >
          All ({jobs.length})
        </button>
      </div>

      {jobFilter === 'expired' && expiredJobsCount > 0 && handleArchiveAllExpired && (
        <div className="flex justify-end">
          <button
            onClick={handleArchiveAllExpired}
            className="px-4 h-9 bg-red-500/10 border border-red-500/40 text-red-300 font-heading font-bold text-xs uppercase hover:bg-red-500/20 flex items-center gap-2"
            data-testid="archive-all-expired-btn"
          >
            <Archive className="w-4 h-4" strokeWidth={2} />
            Archive All Expired ({expiredJobsCount})
          </button>
        </div>
      )}

      {/* Unfinished Jobs by Driver */}
      {jobFilter === 'unfinished' && unfinishedJobs.by_driver.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-heading font-bold text-lg uppercase text-white flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-400" strokeWidth={2} />
            Unfinished Jobs by Driver
          </h3>
          {unfinishedJobs.by_driver.map((driverGroup) => (
            <div key={driverGroup.driver_id} className="bg-[#161920] border border-white/10 p-4">
              <div className="flex items-center gap-3 mb-3">
                <Users className="w-5 h-5 text-primary" strokeWidth={2} />
                <h4 className="font-heading font-bold text-white uppercase">{driverGroup.driver_name}</h4>
                <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                  {driverGroup.jobs.length} unfinished
                </span>
                {(() => {
                  const driver = drivers.find(d => d.id === driverGroup.driver_id);
                  const allSelected = driverGroup.jobs.every(j => selectedJobIds.has(j.id));
                  return (
                    <button
                      onClick={() => {
                        setSelectedJobIds(prev => {
                          const next = new Set(prev);
                          if (allSelected) {
                            driverGroup.jobs.forEach(j => next.delete(j.id));
                          } else {
                            driverGroup.jobs.forEach(j => next.add(j.id));
                          }
                          return next;
                        });
                      }}
                      className="ml-auto px-3 h-8 text-xs font-heading font-bold uppercase border border-green-500/40 text-green-300 hover:bg-green-500/10 flex items-center gap-1"
                      data-testid={`select-all-jobs-driver-${driverGroup.driver_id}`}
                    >
                      <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} />
                      {allSelected ? 'UNSELECT ALL' : 'SELECT ALL FOR WHATSAPP'}
                      {driver && !driver.phone && <span className="text-yellow-300 ml-1">(no phone)</span>}
                    </button>
                  );
                })()}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {driverGroup.jobs.map((job) => {
                  const isSelected = selectedJobIds.has(job.id);
                  return (
                  <div
                    key={job.id}
                    className={`bg-[#1a1d24] border p-3 ${isSelected ? 'border-green-500/60' : 'border-white/5'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2 flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleJobSelection(job)}
                          className="mt-1 w-4 h-4 accent-green-500 cursor-pointer"
                          data-testid={`select-job-checkbox-${job.id}`}
                        />
                        <div>
                          <p className="font-bold text-white">{job.site_name}</p>
                          <p className="text-xs text-white/60">{job.customer_name || 'No customer'}</p>
                          <p className="text-xs text-white/40 mt-1">Date: {job.assigned_date}</p>
                          <p className="text-xs text-primary mt-1 font-semibold">
                            Loads: {job.loads_completed || 0}/{job.loads_required || 1} 
                            <span className="text-yellow-400 ml-1">({(job.loads_required || 1) - (job.loads_completed || 0)} remaining)</span>
                          </p>
                          {job.dumping_ground_name && (
                            <p className="text-xs text-cyan-300 mt-1" data-testid={`job-card-dg-${job.id}`}>
                              Dumping: {job.dumping_ground_name}
                            </p>
                          )}
                          {job.notes && <p className="text-xs text-white/40 italic">{job.notes}</p>}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 text-xs uppercase border ${getStatusColor(job.status)}`}>
                        {job.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex gap-2 mt-2">
                      {job.status === 'pending' && (
                        <button
                          onClick={() => handleDeleteJob(job.id)}
                          className="px-2 py-1 text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* All Jobs List */}
      <div className="bg-[#161920] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-4 text-white/60 font-semibold uppercase text-xs whitespace-nowrap w-10">
                <span className="sr-only">Select</span>
              </th>
              <th className="text-left py-3 px-4 text-white/60 font-semibold uppercase text-xs whitespace-nowrap">Driver</th>
              <th className="text-left py-3 px-4 text-white/60 font-semibold uppercase text-xs whitespace-nowrap">Site</th>
              <th className="text-left py-3 px-4 text-white/60 font-semibold uppercase text-xs whitespace-nowrap">Customer</th>
              <th className="text-left py-3 px-4 text-white/60 font-semibold uppercase text-xs whitespace-nowrap">Date</th>
              <th className="text-left py-3 px-4 text-white/60 font-semibold uppercase text-xs whitespace-nowrap">Loads</th>
              <th className="text-left py-3 px-4 text-white/60 font-semibold uppercase text-xs whitespace-nowrap">Status</th>
              <th className="text-right py-3 px-4 text-white/60 font-semibold uppercase text-xs whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.map((job) => {
              const expired = isJobExpired(job);
              const isSelected = selectedJobIds.has(job.id);
              const selectable = job.status !== 'completed' && !expired;
              return (
                <tr key={job.id} className={`border-b border-white/5 hover:bg-white/5 ${isSelected ? 'bg-green-500/5' : ''}`}>
                  <td className="py-3 px-4 whitespace-nowrap">
                    {selectable ? (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleJobSelection(job)}
                        className="w-4 h-4 accent-green-500 cursor-pointer"
                        data-testid={`select-job-row-${job.id}`}
                      />
                    ) : (
                      <span className="text-white/20">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-white whitespace-nowrap">{job.driver_name}</td>
                  <td className="py-3 px-4 text-white whitespace-nowrap">
                    {job.site_name}
                    {job.dumping_ground_name && (
                      <span className="ml-2 text-xs text-cyan-300" title="Dumping ground">→ {job.dumping_ground_name}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-white/60 whitespace-nowrap">{job.customer_name || '-'}</td>
                  <td className="py-3 px-4 text-white/60 whitespace-nowrap">{job.assigned_date}</td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span className="text-white font-mono">
                      {job.loads_completed || 0}/{job.loads_required || 1}
                      {job.status !== 'completed' && (
                        <span className="text-yellow-400 text-xs ml-1">
                          ({(job.loads_required || 1) - (job.loads_completed || 0)} left)
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span className={`px-2 py-0.5 text-xs uppercase border ${getStatusColor(job.status, expired)}`}>
                      {expired ? 'EXPIRED' : job.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <div className="flex gap-2 justify-end">
                      {job.status !== 'completed' && !expired && !job.is_archived && (
                        <>
                          <button
                            onClick={() => handleEditJob(job)}
                            className="p-1.5 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                            title="Edit job"
                            data-testid={`edit-job-${job.id}`}
                          >
                            <Edit2 className="w-4 h-4" strokeWidth={2} />
                          </button>
                          <button
                            onClick={() => handleUpdateJobStatus(job.id, 'completed')}
                            className="p-1.5 bg-green-500/10 text-green-400 hover:bg-green-500/20"
                            title="Mark as completed"
                          >
                            <CheckCircle className="w-4 h-4" strokeWidth={2} />
                          </button>
                        </>
                      )}
                      {expired && !job.is_archived && handleArchiveJob && (
                        <button
                          onClick={() => handleArchiveJob(job.id)}
                          className="p-1.5 bg-white/10 text-white/70 hover:bg-white/20"
                          title="Archive job"
                          data-testid={`archive-job-${job.id}`}
                        >
                          <Archive className="w-4 h-4" strokeWidth={2} />
                        </button>
                      )}
                      {job.is_archived && handleUnarchiveJob && (
                        <button
                          onClick={() => handleUnarchiveJob(job.id)}
                          className="p-1.5 bg-white/10 text-white/70 hover:bg-white/20"
                          title="Restore job"
                          data-testid={`unarchive-job-${job.id}`}
                        >
                          <RotateCcw className="w-4 h-4" strokeWidth={2} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteJob(job.id)}
                        className="p-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20"
                        title="Delete job"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredJobs.length === 0 && (
              <tr>
                <td colSpan="8" className="py-8 text-center text-white/40">
                  No jobs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
